/**
 * PROBLEMA SECTION — Rediseño B2B SaaS
 *
 * Referencia visual: diseño compacto operacional
 * Fondo: #f7f9fb (off-white cálido, no slate puro)
 * Header: alineado a la izquierda, tipografía compacta
 * Cards: densas, borde sutil, stat en azul primario, borde dashed
 * Callout: fondo azul claro (secondary), icono circular blanco
 * Grid: 3 col desktop → 2 tablet → 1 mobile
 */

import React, { useEffect, useRef } from 'react';
import {
  AlertCircle,
  TrendingDown,
  FileQuestion,
  Users,
  Calculator,
  Clock,
  Lightbulb,
} from 'lucide-react';

// ─── Intersection Observer reutilizable ──────────────────────────────────────
const useReveal = (delay = 0) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(16px)';
    el.style.transition = `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.5s cubic-bezier(0.16,1,0.3,1) ${delay}ms`;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          observer.disconnect();
        }
      },
      { threshold: 0.08 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);
  return ref;
};

// ─── ProblemCard ──────────────────────────────────────────────────────────────
const ProblemCard = ({ icon, titulo, descripcion, stat, statLabel, delay }) => {
  const ref = useReveal(delay);
  return (
    <article
      ref={ref}
      className="flex flex-col gap-3 bg-white rounded-lg p-5 border border-black/[0.08] shadow-[0_10px_25px_rgba(15,23,42,0.04)] hover:shadow-[0_10px_25px_rgba(15,23,42,0.08)] hover:border-black/[0.14] transition-all duration-200"
    >
      {/* Icono */}
      <div className="w-10 h-10 rounded-full bg-[#f1f4f7] flex items-center justify-center text-[#0b63c6] shrink-0">
        {React.cloneElement(icon, { size: 20, strokeWidth: 1.75 })}
      </div>

      {/* Título */}
      <h3 className="text-[16px] font-semibold text-[#0b1220] leading-snug">
        {titulo}
      </h3>

      {/* Descripción */}
      <p className="text-[14px] text-[#6b7280] leading-[1.6] flex-grow">
        {descripcion}
      </p>

      {/* Stat */}
      <div className="pt-2.5 mt-1 border-t border-dashed border-black/[0.08] flex flex-col gap-0.5">
        <span className="text-[18px] font-bold text-[#0b63c6] leading-tight">
          {stat}
        </span>
        <span className="text-[12px] text-[#6b7280] leading-snug">
          {statLabel}
        </span>
      </div>
    </article>
  );
};

// ─── Componente principal ─────────────────────────────────────────────────────
const Problema = () => {
  const headerRef = useReveal(0);
  const calloutRef = useReveal(0);

  const problemas = [
    {
      icon: <Calculator />,
      titulo: 'Cierras el mes con sorpresas',
      descripcion:
        'Creías que ibas bien, pero el contador te dice que hay pérdidas. No sabes de dónde vienen ni cómo evitarlas el próximo mes.',
      stat: '8 de cada 10',
      statLabel: 'empresas no saben su rentabilidad hasta fin de mes',
    },
    {
      icon: <TrendingDown />,
      titulo: 'Viajes que parecen buenos... pero no lo son',
      descripcion:
        'Aceptas un viaje por S/ 5,000 pensando que es ganancia. Al final, entre combustible, peajes, chofer y mantenimiento, perdiste S/ 300.',
      stat: '30-40%',
      statLabel: 'de viajes operan con margen negativo sin saberlo',
    },
    {
      icon: <Clock />,
      titulo: 'Trabajas todo el día y ganas poco',
      descripcion:
        'Tus camiones no paran, tus choferes trabajan duro, pero al final del mes apenas queda para pagar gastos y reinvertir.',
      stat: 'S/ 800-1,200',
      statLabel: 'pérdida promedio mensual por no controlar costos',
    },
    {
      icon: <Users />,
      titulo: 'No sabes qué clientes te convienen',
      descripcion:
        'Tienes clientes que pagan bien pero exigen rutas caras. Otros pagan menos pero son más rentables. ¿Cómo decides?',
      stat: '40%',
      statLabel: 'de clientes pueden estar generando pérdidas',
    },
    {
      icon: <FileQuestion />,
      titulo: 'Excel ya no alcanza',
      descripcion:
        'Intentas llevar control en Excel pero se vuelve un caos: facturas por aquí, gastos por allá, y al final todo desactualizado.',
      stat: '6-8 horas/sem',
      statLabel: 'perdidas en control manual desactualizado',
    },
    {
      icon: <AlertCircle />,
      titulo: 'Decisiones basadas en "feeling"',
      descripcion:
        '¿Compro otro camión? ¿Subo precios? ¿Dejo ese cliente? Decides por intuición porque no tienes datos concretos.',
      stat: '70%',
      statLabel: 'de decisiones se toman sin datos reales',
    },
  ];

  return (
    <section
      id="problema"
      className="w-full py-24 lg:py-28"
      style={{ backgroundColor: '#f7f9fb' }}
    >
      <div className="max-w-[1120px] mx-auto px-6 flex flex-col gap-12">

        {/* ── Header — alineado a la izquierda ── */}
        <header
          ref={headerRef}
          className="max-w-[720px] flex flex-col gap-4"
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#e9f2ff] border border-black/[0.08] text-[#0b1220] w-fit">
            <AlertCircle size={16} strokeWidth={2} className="text-[#0b63c6]" />
            <span className="text-[13px] font-medium">Realidad del sector</span>
          </div>

          <h2 className="text-[28px] font-bold text-[#0b1220] leading-[1.3] tracking-[-0.01em]">
            ¿Te suena familiar alguna de estas situaciones?
          </h2>

          <p className="text-[15px] text-[#6b7280] leading-[1.6] max-w-[640px]">
            La mayoría de empresas de transporte operan{' '}
            <strong className="text-[#0b1220] font-semibold">"a ciegas"</strong>{' '}
            sin datos reales de rentabilidad por viaje, vehículo o cliente.
          </p>
        </header>

        {/* ── Grid de cards ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {problemas.map((p, i) => (
            <ProblemCard key={i} {...p} delay={i * 55} />
          ))}
        </div>

        {/* ── Callout ── */}
        <div ref={calloutRef}>
          <div className="flex items-center gap-3 px-5 py-[18px] rounded-xl bg-[#e9f2ff] border border-black/[0.08]">
            {/* Icono circular blanco */}
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shrink-0 shadow-[0_1px_4px_rgba(0,0,0,0.06)]">
              <Lightbulb size={16} strokeWidth={1.75} className="text-[#0b1220]" />
            </div>

            <div className="flex flex-col gap-0.5">
              <h3 className="text-[15px] font-semibold text-[#0b1220] leading-snug">
                El problema no es que trabajes poco
              </h3>
              <p className="text-[14px] text-[#6b7280] leading-[1.6]">
                El problema es que{' '}
                <strong className="text-[#0b1220] font-semibold">no tienes visibilidad</strong>{' '}
                de dónde ganas y dónde pierdes. Y eso te cuesta miles de soles cada mes.
              </p>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default Problema;